"use client";

import type { PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";

/**
 * Controlled popover pinned to an existing element (no trigger of its own),
 * for hover-or-tap info cards. The anchor keeps its own click handling; a
 * pointerdown on it is not treated as an outside click.
 */
export function StudioAnchoredPopover({
  open,
  onOpenChange,
  anchor,
  anchorRef,
  label,
  side = "bottom",
  align = "end",
  sideOffset = 6,
  contentClassName,
  onContentPointerEnter,
  onContentPointerLeave,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** One element; the popover positions against it. */
  anchor: ReactElement;
  anchorRef?: { current: HTMLElement | null };
  label: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  contentClassName?: string;
  onContentPointerEnter?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onContentPointerLeave?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{anchor}</PopoverAnchor>
      <PopoverContent
        data-studio-overlay=""
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        aria-label={label}
        className={`studio-overlay-popover${contentClassName ? ` ${contentClassName}` : ""}`}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef?.current?.contains(event.target as Node)) event.preventDefault();
        }}
        onPointerEnter={onContentPointerEnter}
        onPointerLeave={onContentPointerLeave}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
